// Lightweight SVG staff renderer for violin (treble clef). Implements the spec's
// highlight pipeline (data-note-id attributes + CSS class toggle). Designed to be
// swappable for Verovio later (NFR-9) — same noteId contract.

import type { MusicXMLScore, NoteEvent } from '../types';
import { flattenTimeline } from './parser';

const STEP_INDEX: Record<string, number> = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };
const SHARP_ORDER = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
const FLAT_ORDER = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];

function diatonicIndex(midi: number): number {
  const pc = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const map = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6]; // pc -> diatonic step (sharps/flats collapse)
  return octave * 7 + map[pc];
}

function stepOf(midi: number): string {
  const names = ['C', 'C', 'D', 'D', 'E', 'F', 'F', 'G', 'G', 'A', 'A', 'B'];
  return names[((midi % 12) + 12) % 12];
}

export class ScoreRenderer {
  readonly el: SVGElement;
  private noteGroups = new Map<string, SVGElement>();
  private timeline: { note: NoteEvent; measure: number }[];
  private beatsPerMeasure: number;

  constructor(private score: MusicXMLScore, width = 680) {
    this.timeline = flattenTimeline(score);
    this.beatsPerMeasure = score.timeSignature[0] || 4;
    this.el = this.render(width);
  }

  getTimeline() {
    return this.timeline;
  }

  // Find the note(s) active at a given global beat.
  notesAtBeat(beat: number): string[] {
    return this.timeline
      .filter((t) => beat >= t.note.startTime - 0.01 && beat < t.note.startTime + t.note.duration)
      .map((t) => t.note.id);
  }

  setHighlight(ids: Set<string>): void {
    for (const [id, g] of this.noteGroups) {
      g.classList.toggle('note-active', ids.has(id));
    }
  }

  markErrors(errorIds: Set<string>): void {
    for (const [id, g] of this.noteGroups) {
      g.classList.toggle('note-error', errorIds.has(id));
    }
  }

  private render(width: number): SVGElement {
    const margin = 14;
    const measureWidth = 150;
    const systemGap = 92;
    const staffH = 32; // 4 spaces * 8
    const padLeft = 46; // clef + time sig
    const measuresPerRow = Math.max(1, Math.floor((width - margin * 2 - padLeft) / measureWidth));
    const part = this.score.parts[0];
    const measureCount = part?.measures.length ?? 0;
    const rows = Math.max(1, Math.ceil(measureCount / measuresPerRow));
    const height = margin * 2 + rows * (staffH + systemGap) + 20;
    const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svgEl.setAttribute('viewBox', `0 0 ${width} ${height}`);
    svgEl.setAttribute('width', String(width));
    svgEl.setAttribute('style', 'width:100%;max-width:680px');
    svgEl.classList.add('score-svg');

    let mi = 0;
    for (let row = 0; row < rows; row++) {
      const yTop = margin + row * (staffH + systemGap);
      const xStart = margin;
      const rowWidth = width - margin * 2;
      // Staff lines
      for (let i = 0; i < 5; i++) {
        const y = yTop + i * 8;
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(xStart));
        line.setAttribute('y1', String(y));
        line.setAttribute('x2', String(xStart + rowWidth));
        line.setAttribute('y2', String(y));
        line.setAttribute('stroke', 'var(--text-faint)');
        line.setAttribute('stroke-width', '1');
        svgEl.append(line);
      }
      // Treble clef (unicode) + key sig + time sig at row start
      const clef = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      clef.setAttribute('x', String(xStart + 2));
      clef.setAttribute('y', String(yTop + staffH - 2));
      clef.setAttribute('font-size', '34');
      clef.setAttribute('fill', 'var(--text-dim)');
      clef.textContent = '𝄞';
      svgEl.append(clef);

      // Key signature accidentals
      let ksX = xStart + 26;
      if (this.score.keySignature > 0) {
        for (let i = 0; i < this.score.keySignature && i < 7; i++) {
          ksX = this.drawAccidental(svgEl, ksX, yTop, SHARP_ORDER[i] + '5', '♯');
        }
      } else if (this.score.keySignature < 0) {
        for (let i = 0; i < -this.score.keySignature && i < 7; i++) {
          ksX = this.drawAccidental(svgEl, ksX, yTop, FLAT_ORDER[i] + '4', '♭');
        }
      }
      // Time signature
      const ts = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      ts.setAttribute('x', String(ksX + 4));
      ts.setAttribute('y', String(yTop + 14));
      ts.setAttribute('font-size', '13');
      ts.setAttribute('font-weight', '700');
      ts.setAttribute('fill', 'var(--text)');
      ts.textContent = `${this.score.timeSignature[0]}`;
      svgEl.append(ts);
      const ts2 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      ts2.setAttribute('x', String(ksX + 4));
      ts2.setAttribute('y', String(yTop + 28));
      ts2.setAttribute('font-size', '13');
      ts2.setAttribute('font-weight', '700');
      ts2.setAttribute('fill', 'var(--text)');
      ts2.textContent = `${this.score.timeSignature[1]}`;
      svgEl.append(ts2);

      // Measures in this row
      const inRow = Math.min(measuresPerRow, measureCount - row * measuresPerRow);
      for (let c = 0; c < inRow; c++) {
        const m = part.measures[mi];
        const mx = xStart + padLeft + c * measureWidth;
        this.drawMeasure(svgEl, m, mx, yTop, measureWidth - 6, staffH);
        // Barline
        const barX = mx + measureWidth - 6;
        const bar = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        bar.setAttribute('x1', String(barX));
        bar.setAttribute('y1', String(yTop));
        bar.setAttribute('x2', String(barX));
        bar.setAttribute('y2', String(yTop + staffH));
        bar.setAttribute('stroke', 'var(--text-faint)');
        svgEl.append(bar);
        mi++;
      }
    }

    // Inject styles once
    this.injectStyles();
    return svgEl;
  }

  private drawAccidental(svg: SVGElement, x: number, yTop: number, noteName: string, glyph: string): number {
    // place accidental near a nominal y based on noteName pitch (approx)
    const midi = nameToMidi(noteName);
    const y = this.noteY(midi, yTop);
    const t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    t.setAttribute('x', String(x));
    t.setAttribute('y', String(y + 4));
    t.setAttribute('font-size', '14');
    t.setAttribute('fill', 'var(--text-dim)');
    t.textContent = glyph;
    svg.append(t);
    return x + 9;
  }

  private drawMeasure(svg: SVGElement, m: { notes: NoteEvent[] }, mx: number, yTop: number, width: number, staffH: number): void {
    const usable = width - 16;
    for (const n of m.notes) {
      const x = mx + 10 + (n.startTime / Math.max(1, this.beatsPerMeasure)) * usable;
      const y = this.noteY(n.pitch, yTop);
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('data-note-id', n.id);
      g.classList.add('note');

      // Accidental if pitch deviates from key-default
      const acc = accidentalFor(n.pitch);
      if (acc) {
        const at = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        at.setAttribute('x', String(x - 10));
        at.setAttribute('y', String(y + 4));
        at.setAttribute('font-size', '13');
        at.setAttribute('fill', 'var(--text-dim)');
        at.textContent = acc;
        g.append(at);
      }

      const head = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
      head.setAttribute('cx', String(x));
      head.setAttribute('cy', String(y));
      head.setAttribute('rx', '4.2');
      head.setAttribute('ry', '3.2');
      head.setAttribute('transform', `rotate(-22 ${x} ${y})`);
      head.classList.add('notehead');
      g.append(head);

      // Stem
      const stemUp = y > yTop + staffH / 2;
      const stem = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const sx = stemUp ? x + 3.5 : x - 3.5;
      stem.setAttribute('x1', String(sx));
      stem.setAttribute('y1', String(y));
      stem.setAttribute('x2', String(sx));
      stem.setAttribute('y2', String(stemUp ? y - 22 : y + 22));
      stem.setAttribute('stroke', 'var(--text)');
      stem.setAttribute('stroke-width', '1.2');
      g.append(stem);

      svg.append(g);
      this.noteGroups.set(n.id, g);
    }
  }

  private noteY(midi: number, yTop: number): number {
    // F5 (top line) diatonic index = 38; each diatonic step = 4px up.
    const f5 = 5 * 7 + 3;
    const idx = diatonicIndex(midi);
    return yTop + (f5 - idx) * 4;
  }

  private stylesInjected = false;
  private injectStyles(): void {
    if (this.stylesInjected || document.getElementById('score-styles')) return;
    this.stylesInjected = true;
    const style = document.createElement('style');
    style.id = 'score-styles';
    style.textContent = `
      .score-svg .note .notehead { fill: var(--text); transition: fill .08s; }
      .score-svg .note.note-active .notehead { fill: var(--accent-2); filter: drop-shadow(0 0 4px var(--accent-2)); }
      .score-svg .note.note-error .notehead { fill: var(--bad); }
      .score-svg .note.note-active { /* active marker handled by notehead color */ }
    `;
    document.head.append(style);
  }
}

function nameToMidi(name: string): number {
  const m = name.match(/^([A-G])(#|b)?(-?\d)$/);
  if (!m) return 69;
  const step = STEP_INDEX[m[1]];
  const alter = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return (Number(m[3]) + 1) * 12 + step + alter;
}

function accidentalFor(midi: number): string {
  // Determine if the note needs an accidental beyond the key signature.
  const pc = ((midi % 12) + 12) % 12;
  const step = stepOf(midi);
  const NAT: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  const naturalPc = NAT[step];
  const diff = pc - naturalPc;
  if (naturalPc === undefined || diff === 0) return '';
  if (diff === 1 || diff === -11) return '♯';
  if (diff === -1 || diff === 11) return '♭';
  return '';
}

